import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useMemo } from 'react';
import {
  DollarSign, FileText, AlertTriangle, ExternalLink, RefreshCw,
  Package as PackageIcon, Cpu, TrendingUp,
} from 'lucide-react';
import * as sharedTypes from '@platform/shared-types';
import type { DealDocumentSectionConfig } from '@platform/shared-types';
import SearchableDropdown from '../../components/SearchableDropdown';
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown';
import ShimmerImage from '../../components/ShimmerImage';
import { getIcon } from '../../services/iconRegistry';
import { resolveAccent, tint } from '../../utils/accentColor';
import { money } from '../../components/finance/financeFormat';
import type { DealDocumentCatalogue } from './DealDocumentsSections';

/**
 * PARTNER COMMISSION — the deal's own section (2026-08-30).
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS REPLACES, AND WHY IT IS A SECTION OF ITS OWN
 * ---------------------------------------------------------------------------
 *
 * The deal page used to carry a commission PLAN picker sitting on top of the
 * old flat trio — `commissionMode` / `percentageRate` / `flatFee` — greyed out
 * to 50% opacity and labelled "fallback". Two ways to express one thing, one
 * of them dead, both editable, and no way to see what either would actually
 * pay. The flat trio is gone from this screen: a plan says everything it said
 * and things it never could ("20% on software, $50 a terminal"). The stored
 * fields are untouched and still read by the pay path for deals built before
 * plans existed — they are simply no longer authored here.
 *
 * What replaces them is the rest of this component: the paperwork a partner
 * signs to sell the deal, and a breakdown of what the plan pays on each thing
 * the deal actually sells.
 *
 * ---------------------------------------------------------------------------
 * THE CALCULATOR IS A PROJECTION, AND SAYS SO
 * ---------------------------------------------------------------------------
 *
 * Every figure comes from `previewPlanEarnings`, which resolves rules with
 * `findRuleForTargets` and computes with `computeRuleCommission` — the exact
 * two functions `commissionEmit.ts` pays from. Nothing here re-derives a rate.
 *
 * It still cannot be a promise, and the footnotes say which parts are
 * projection: one unit per device (a deal does not know how many terminals a
 * merchant will buy), every finance option shown though a merchant takes one,
 * and — flagged loudly — the fact that the completion path currently emits ONE
 * aggregate software line, so a rule aimed at a specific package cannot be
 * reached by it yet.
 */

/** Guarded runtime imports — a stale Vite pre-bundle resolves new exports to
 *  `undefined`, and calling one at module scope white-screens the deal page. */
const previewPlanEarnings = typeof sharedTypes.previewPlanEarnings === 'function'
  ? sharedTypes.previewPlanEarnings
  : null;
const planTargetsPackagesSpecifically = typeof sharedTypes.planTargetsPackagesSpecifically === 'function'
  ? sharedTypes.planTargetsPackagesSpecifically
  : () => false;
if (!previewPlanEarnings) {
  console.error(
    '[DealCommissionSection] @platform/shared-types resolved to a stale bundle — '
    + 'RESTART the dev server (deleting node_modules/.vite under a running server does nothing). '
    + 'The earnings breakdown is hidden until you do; the plan picker and paperwork still work.',
  );
}

const LIGHT_PANEL = 'rounded-2xl border border-stone-200 bg-white text-stone-800';
const LIGHT_HEAD = 'font-mono text-[10px] tracking-[.14em] uppercase text-stone-400';
const LIGHT_LABEL = 'flex items-center gap-2 text-[11px] font-medium text-stone-600 mb-1';

export interface CommissionCalcPackage {
  packageId: string;
  name: string;
  /** What the merchant pays per month under this deal. Null = unknown price. */
  netCents: number | null;
  /** Lucide icon name from the packages catalog. */
  icon?: string;
  /** The package row itself, for `resolveAccent`'s colour maths. */
  accent?: any;
}

export interface CommissionCalcDevice {
  skuId: string;
  name: string;
  /** modelId / bundleId — a rule may target the DEVICE rather than the offer. */
  deviceId?: string;
  saleMode?: string;
  monthlyCents: number;
  oneTimeCents: number;
  /** Landed cost, for a margin-basis rule. Absent = unknown, never zero. */
  costCents?: number;
  imageUrl?: string;
  /**
   * The DEVICE's own name, not the offer's. One terminal sold three ways is
   * three rows here, and three near-identical SKU names ("… — 24-month
   * installment") read as three different devices. The rows are grouped under
   * this instead.
   */
  deviceName?: string;
}

/**
 * WHICH RULE PAID THIS ROW, and how — shared by the software rows and the
 * device rows so a rule cannot be described two ways in one table.
 */
const RuleCell: React.FC<{ row: any }> = ({ row }) => (
  row.ruleId === null ? (
    <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-amber-800">
      <AlertTriangle size={10} /> no rule
    </span>
  ) : (
    <span className="text-[11px] text-stone-600">
      {row.rateLabel}
      {!row.specific && <span className="ml-1 text-[10px] text-stone-400">(catch-all)</span>}
      {row.recurrence === 'monthly' && (
        <span className="ml-1 text-[10px] text-stone-400">
          · {row.months ? `${row.months} mo` : 'for life'}
        </span>
      )}
    </span>
  )
);

/** One row of the breakdown, with the thing's own icon or photo beside it. */
const RowThumb: React.FC<{
  imageUrl?: string; iconName?: string; accent?: any; fallback: React.FC<any>;
}> = ({ imageUrl, iconName, accent, fallback: Fallback }) => {
  if (imageUrl !== undefined) {
    return (
      <ShimmerImage
        src={imageUrl}
        alt=""
        width={26}
        height={26}
        objectFit="contain"
        className="shrink-0 rounded-md border border-stone-200 bg-stone-50"
        fallbackBgColor="bg-stone-100"
        fallbackIcon={<Fallback size={12} className="text-stone-400" />}
        variant="light"
      />
    );
  }
  const Icon = iconName ? getIcon(iconName) : Fallback;
  const a = accent ? resolveAccent(accent) : null;
  return (
    <div
      className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md border border-stone-200"
      style={{ background: a ? tint(a.from, 0.14) : '#f5f5f4' }}
    >
      <Icon size={13} style={{ color: a ? a.from : '#78716c' }} />
    </div>
  );
};

export const DealCommissionSection: React.FC<{
  /** Every plan the picker may offer, already filtered for retired ones. */
  planOptions: Array<{ value: string; label: string; subtitle?: string }>;
  /** The full plan rows, so the calculator can read the selected plan's rules. */
  plans: any[];
  planId: string;
  onPlanChange: (planId: string) => void;
  onManagePlans: () => void;

  /** The partner's paperwork for this deal — NOT the customer's binder. */
  documents: DealDocumentSectionConfig | undefined;
  onDocumentsChange: (next: DealDocumentSectionConfig) => void;
  catalogue: DealDocumentCatalogue;

  packages: CommissionCalcPackage[];
  devices: CommissionCalcDevice[];
}> = ({
  planOptions, plans, planId, onPlanChange, onManagePlans,
  documents, onDocumentsChange, catalogue, packages, devices,
}) => {
  const plan = useMemo(
    () => plans.find((p) => p?.planId === planId) || null,
    [plans, planId],
  );

  const cfg = documents || { agreementDocumentIds: [], policyDocumentIds: [] };
  const setCfg = (patch: Partial<DealDocumentSectionConfig>) => onDocumentsChange({
    agreementDocumentIds: cfg.agreementDocumentIds || [],
    policyDocumentIds: cfg.policyDocumentIds || [],
    ...patch,
  });

  /**
   * The deal's sellable lines, in the calculator's shape.
   *
   * A package with an UNKNOWN price is excluded rather than priced at zero:
   * "we could not read the price" and "this pays nothing" are different claims
   * and must not look the same. Its absence is reported below the table.
   */
  const lines = useMemo(() => {
    const out: any[] = [];
    for (const p of packages) {
      if (typeof p.netCents !== 'number') continue;
      out.push({
        key: p.packageId,
        label: p.name,
        scope: 'software_package',
        targetIds: [p.packageId],
        monthlyCents: p.netCents,
        oneTimeCents: 0,
      });
    }
    for (const d of devices) {
      out.push({
        key: d.skuId,
        label: d.name,
        scope: 'device_offer',
        // Most specific first — the SKU (this finance option), then the device
        // it is an option ON, so a rule targeting a model covers every way of
        // paying for it. Same order `commissionEmit` walks.
        targetIds: [d.skuId, d.deviceId],
        monthlyCents: d.monthlyCents,
        oneTimeCents: d.oneTimeCents,
        ...(typeof d.costCents === 'number' ? { costCents: d.costCents } : {}),
      });
    }
    return out;
  }, [packages, devices]);

  const preview = useMemo(
    () => (plan && previewPlanEarnings ? previewPlanEarnings(plan, lines) : null),
    [plan, lines],
  );

  const unpricedPackages = packages.filter((p) => typeof p.netCents !== 'number');
  const packageMeta = useMemo(
    () => new Map(packages.map((p) => [p.packageId, p])),
    [packages],
  );
  const deviceMeta = useMemo(
    () => new Map(devices.map((d) => [d.skuId, d])),
    [devices],
  );

  /**
   * ONE BLOCK PER DEVICE, not one row per offer.
   *
   * A terminal sold outright, financed and rented is three offers, so the
   * table listed the same piece of hardware three times under three long,
   * near-identical names — the commission mattered, but the shape of what was
   * being paid on did not survive the repetition. The offers are nested under
   * the device instead: its picture and name once, then what each way of
   * selling it pays, and the device's own subtotal.
   */
  const grouped = useMemo(() => {
    const rows = preview?.rows || [];
    const software = rows.filter((r) => r.scope === 'software_package');
    const blocks = new Map<string, {
      key: string; name: string; imageUrl?: string;
      rows: typeof rows; upfront: number; perMonth: number; firstYear: number; unpriced: number;
    }>();
    for (const r of rows) {
      if (r.scope !== 'device_offer') continue;
      const dev = deviceMeta.get(r.key);
      const key = dev?.deviceId || r.key;
      let b = blocks.get(key);
      if (!b) {
        b = {
          key,
          name: dev?.deviceName || dev?.name || r.label,
          imageUrl: dev?.imageUrl,
          rows: [], upfront: 0, perMonth: 0, firstYear: 0, unpriced: 0,
        };
        blocks.set(key, b);
      }
      b.rows.push(r);
      // A null is "no rule / no cost", never zero — it is counted, not summed.
      if (r.upfrontCents === null || r.perMonthCents === null || r.firstYearCents === null) b.unpriced += 1;
      b.upfront += r.upfrontCents || 0;
      b.perMonth += r.perMonthCents || 0;
      b.firstYear += r.firstYearCents || 0;
    }
    return { software, devices: [...blocks.values()] };
  }, [preview, deviceMeta]);

  const disabled = catalogue.loading || Boolean(catalogue.error);
  const optionsFrom = (docs: { documentId: string; title: string; category?: string }[]) =>
    docs.map((d) => ({ value: d.documentId, label: d.title, subtitle: d.category || 'Uncategorised' }));

  return (
    <div className={`${LIGHT_PANEL} p-4 space-y-4`}>
      <div>
        <span className={`${LIGHT_HEAD} flex items-center gap-2`}>
          <DollarSign className="h-3 w-3" /> Partner commission
        </span>
        <p className="mt-1 text-[11.5px] text-stone-500">
          What a partner earns for selling this deal, and what they sign to be allowed to.
          Internal — never shown to the merchant.
        </p>
      </div>

      {/* ---- the plan ---------------------------------------------------- */}
      <div className="max-w-xl">
        {/* The asterisk carries its own accessible name.
            NO `sr-only` SPAN HERE. Tailwind's `.sr-only` is `position:absolute`
            with no offsets, so with no positioned ancestor its containing block
            is the INITIAL containing block — it lands at the document origin's
            coordinate space, ~5800px down, which made <html> scrollable when
            nothing else on this screen is (the app shell is `h-screen` with the
            scrolling inside <main>). Opening any dropdown then autofocused its
            search input, the browser scrolled the DOCUMENT to reveal it, and the
            whole app slid out of view leaving the black page background — the
            "black bar over half the screen" this caused. */}
        <label className={LIGHT_LABEL}>
          Commission plan{' '}
          <span className="text-rose-600" title="Required" aria-label="required">*</span>
        </label>
        <SearchableDropdown
          options={planOptions}
          value={planId || ''}
          onChange={(v) => onPlanChange(v || '')}
          placeholder="-- Select the commission plan --"
          searchPlaceholder="Search plans…"
          variant="light"
        />
        {planId ? (
          <p className="mt-1 text-[11px] text-stone-500">
            This plan prices what partners earn on this deal.
            {' '}
            <button
              type="button"
              onClick={onManagePlans}
              className="font-medium text-rose-600 hover:text-rose-700"
            >
              Manage plans
            </button>
          </p>
        ) : (
          /* Required, and said in the same words the blocked Save button uses.
             A deal with no plan pays through the partner's pin and then the
             platform default, and if neither exists the partner earns nothing
             on a deal someone already sold — an invisible answer to a question
             about money, which is why it is no longer an option. */
          <p className="mt-1 flex items-start gap-1.5 text-[11px] text-rose-700">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>
              Required — a deal cannot be saved without naming the plan it pays partners on.
              {' '}
              <button
                type="button"
                onClick={onManagePlans}
                className="font-medium underline decoration-dotted hover:text-rose-900"
              >
                Manage plans
              </button>
            </span>
          </p>
        )}
      </div>

      {/* Everything below appears only once a plan is chosen: paperwork and a
          breakdown are both statements ABOUT a plan, and neither can be
          authored before one exists. */}
      {planId && (
        <>
          {/* ---- the partner's paperwork ---------------------------------- */}
          <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-3.5">
            <span className={`${LIGHT_HEAD} flex items-center gap-2`}>
              <FileText className="h-3 w-3" /> What the partner signs
            </span>
            <p className="mt-1 text-[11.5px] text-stone-500">
              The commission agreement for this deal and the policies a partner is held to while
              selling it. Separate from the merchant’s paperwork above — different signer, different
              moment, and never shown at checkout.
            </p>

            {catalogue.loading && (
              <p className="mt-2 flex items-center gap-2 text-[11.5px] text-stone-500">
                <RubyGemMark size={13} drawing /> Loading published documents…
              </p>
            )}
            {catalogue.error && (
              <div className="mt-2 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-[11.5px] text-red-800">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                {catalogue.error} — nothing can be selected until this loads. Anything already saved
                on the deal is untouched.
              </div>
            )}

            <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={LIGHT_LABEL}>Agreements to sign</label>
                <MultiSelectDropdown
                  options={optionsFrom(catalogue.agreements)}
                  value={cfg.agreementDocumentIds || []}
                  onChange={(ids) => setCfg({ agreementDocumentIds: ids })}
                  disabled={disabled}
                  placeholder="-- Select the partner agreements --"
                  searchPlaceholder="Search agreements…"
                  summarize={(sel) => (sel.length === 1 ? sel[0].label : `${sel.length} agreements`)}
                />
                <p className="mt-1 text-[11px] text-stone-500">
                  Signed by the partner before they may sell this deal.
                </p>
              </div>
              <div>
                <label className={LIGHT_LABEL}>Policies to attach</label>
                <MultiSelectDropdown
                  options={optionsFrom(catalogue.policies)}
                  value={cfg.policyDocumentIds || []}
                  onChange={(ids) => setCfg({ policyDocumentIds: ids })}
                  disabled={disabled}
                  placeholder="-- Select policies --"
                  searchPlaceholder="Search policies…"
                  summarize={(sel) => (sel.length === 1 ? sel[0].label : `${sel.length} policies`)}
                />
                <p className="mt-1 text-[11px] text-stone-500">
                  Disclosed and accepted, never counter-signed.
                </p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => window.open('/legal/documents', '_blank', 'noopener')}
                className="flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50"
              >
                <ExternalLink size={12} /> Browse documents
              </button>
              <button
                type="button"
                onClick={catalogue.refresh}
                disabled={catalogue.refreshing}
                className="flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              >
                {catalogue.refreshing ? <RubyGemMark size={12} drawing color="currentColor" /> : <RefreshCw size={12} />} Refresh
              </button>
            </div>
          </div>

          {/* ---- what it pays -------------------------------------------- */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3.5">
            <span className={`${LIGHT_HEAD} flex items-center gap-2`}>
              <TrendingUp className="h-3 w-3" /> What a partner earns on this deal
            </span>

            {!previewPlanEarnings ? (
              <p className="mt-2 text-[11.5px] text-amber-800">
                The breakdown is unavailable in this session — restart the dev server. The plan
                itself is saved and pays normally.
              </p>
            ) : !plan ? (
              <p className="mt-2 text-[11.5px] text-amber-800">
                Plan <span className="font-mono">{planId}</span> is not in the loaded list — it may be
                retired or the plans service may be unreachable, so nothing can be broken down. The
                deal still saves and still points at it.
              </p>
            ) : lines.length === 0 ? (
              <p className="mt-2 text-[11.5px] text-stone-500">
                This deal names no priced packages or devices yet, so there is nothing to pay a
                commission on. Add them above and the breakdown appears here.
              </p>
            ) : (
              <>
                <div className="mt-2.5 overflow-x-auto">
                  <table className="w-full min-w-[560px] border-collapse text-[12px]">
                    <thead>
                      <tr className="border-b border-emerald-200/70 text-[10px] uppercase tracking-wider text-stone-500">
                        <th className="py-1.5 pr-2 text-left font-bold">Sold</th>
                        <th className="px-2 py-1.5 text-right font-bold">Merchant pays</th>
                        <th className="px-2 py-1.5 text-left font-bold">Rule</th>
                        <th className="px-2 py-1.5 text-right font-bold">Up front</th>
                        <th className="px-2 py-1.5 text-right font-bold">Per month</th>
                        <th className="py-1.5 pl-2 text-right font-bold">First year</th>
                      </tr>
                    </thead>
                    {/* SOFTWARE — one row per package, as before. */}
                    <tbody>
                      {grouped.software.map((row) => {
                        const pkg = packageMeta.get(row.key);
                        const unpaid = row.ruleId === null || row.upfrontCents === null;
                        return (
                          <tr key={`software:${row.key}`} className="border-b border-emerald-100/70">
                            <td className="max-w-[300px] py-2 pr-2">
                              <div className="flex items-center gap-2" title={row.label}>
                                <RowThumb
                                  iconName={pkg?.icon}
                                  accent={pkg?.accent}
                                  fallback={PackageIcon}
                                />
                                <span className="min-w-0">
                                  <span className="block truncate font-medium text-stone-800">{row.label}</span>
                                  <span className="block text-[10px] text-stone-400">Software package</span>
                                </span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-stone-600">
                              {`${money(pkg?.netCents ?? 0)}/mo`}
                            </td>
                            <td className="px-2 py-2"><RuleCell row={row} /></td>
                            <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums font-semibold text-stone-800">
                              {row.upfrontCents === null ? '—' : (row.upfrontCents ? money(row.upfrontCents) : '·')}
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums font-semibold text-stone-800">
                              {row.perMonthCents === null ? '—' : (row.perMonthCents ? money(row.perMonthCents) : '·')}
                            </td>
                            <td className={`whitespace-nowrap py-2 pl-2 text-right tabular-nums font-bold ${unpaid ? 'text-stone-400' : 'text-emerald-700'}`}>
                              {row.firstYearCents === null ? '—' : money(row.firstYearCents)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>

                    {/* HARDWARE — a block per device: its picture and name once,
                        then every way it can be sold, then its own subtotal. */}
                    {grouped.devices.map((blk) => (
                      <tbody key={`device:${blk.key}`} className="border-b border-emerald-100/70">
                        <tr className="bg-stone-50/70">
                          <td className="max-w-[300px] py-2 pr-2" colSpan={2}>
                            <div className="flex items-center gap-2" title={blk.name}>
                              <RowThumb imageUrl={blk.imageUrl ?? undefined} fallback={Cpu} />
                              <span className="min-w-0">
                                <span className="block truncate font-semibold text-stone-900">{blk.name}</span>
                                <span className="block text-[10px] text-stone-400">
                                  {blk.rows.length} way{blk.rows.length === 1 ? '' : 's'} to sell it
                                  {blk.unpriced > 0 && ` · ${blk.unpriced} with no rule`}
                                </span>
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-2 text-[10px] uppercase tracking-wider text-stone-400">Device subtotal</td>
                          <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums font-bold text-stone-800">
                            {blk.upfront ? money(blk.upfront) : '·'}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums font-bold text-stone-800">
                            {blk.perMonth ? money(blk.perMonth) : '·'}
                          </td>
                          <td className="whitespace-nowrap py-2 pl-2 text-right tabular-nums font-bold text-emerald-700">
                            {money(blk.firstYear)}
                          </td>
                        </tr>
                        {blk.rows.map((row) => {
                          const dev = deviceMeta.get(row.key);
                          const price = [
                            dev?.oneTimeCents ? `${money(dev.oneTimeCents)} up front` : '',
                            dev?.monthlyCents ? `${money(dev.monthlyCents)}/mo` : '',
                          ].filter(Boolean).join(' + ') || 'included';
                          const unpaid = row.ruleId === null || row.upfrontCents === null;
                          return (
                            <tr key={`device:${row.key}`} className="border-t border-emerald-50">
                              <td className="max-w-[300px] py-1.5 pr-2 pl-9">
                                <span className="block truncate text-[11.5px] capitalize text-stone-700" title={row.label}>
                                  {dev?.saleMode || 'sale'}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-stone-600">{price}</td>
                              <td className="px-2 py-1.5"><RuleCell row={row} /></td>
                              <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-stone-800">
                                {row.upfrontCents === null ? '—' : (row.upfrontCents ? money(row.upfrontCents) : '·')}
                              </td>
                              <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-stone-800">
                                {row.perMonthCents === null ? '—' : (row.perMonthCents ? money(row.perMonthCents) : '·')}
                              </td>
                              <td className={`whitespace-nowrap py-1.5 pl-2 text-right tabular-nums font-semibold ${unpaid ? 'text-stone-400' : 'text-emerald-700'}`}>
                                {row.firstYearCents === null ? '—' : money(row.firstYearCents)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    ))}
                    <tfoot>
                      <tr className="border-t-2 border-emerald-200">
                        <td className="py-2 pr-2 text-[11px] font-bold uppercase tracking-wider text-stone-600" colSpan={3}>
                          Per merchant signed
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums font-bold text-stone-900">
                          {money(preview?.totals.upfrontCents || 0)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums font-bold text-stone-900">
                          {money(preview?.totals.perMonthCents || 0)}
                        </td>
                        <td className="whitespace-nowrap py-2 pl-2 text-right tabular-nums text-base font-bold text-emerald-700">
                          {money(preview?.totals.firstYearCents || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Every reason a number above is not the whole truth, said
                    once, here — rather than left for someone to discover from
                    a payout that does not match. */}
                <div className="mt-2.5 space-y-1 text-[10.5px] text-stone-500">
                  {!!preview?.totals.unmatched && (
                    <p className="text-amber-700">
                      <AlertTriangle size={10} className="mr-1 inline" />
                      {preview.totals.unmatched} line{preview.totals.unmatched === 1 ? '' : 's'} above
                      {preview.totals.unmatched === 1 ? ' matches' : ' match'} no rule in this plan and
                      pay nothing. Add a rule for them, or a catch-all, on the plan.
                    </p>
                  )}
                  {!!preview?.totals.unpriceable && (
                    <p className="text-amber-700">
                      <AlertTriangle size={10} className="mr-1 inline" />
                      {preview.totals.unpriceable} line{preview.totals.unpriceable === 1 ? '' : 's'} matched
                      a margin rule with no known landed cost. Nothing can be paid on
                      {preview.totals.unpriceable === 1 ? ' it' : ' them'} until a cost is set on the device.
                    </p>
                  )}
                  {unpricedPackages.length > 0 && (
                    <p className="text-amber-700">
                      <AlertTriangle size={10} className="mr-1 inline" />
                      {unpricedPackages.length} package{unpricedPackages.length === 1 ? ' has' : 's have'} no
                      readable price and {unpricedPackages.length === 1 ? 'is' : 'are'} left out entirely rather
                      than counted as free.
                    </p>
                  )}
                  {planTargetsPackagesSpecifically(plan) && (
                    <p className="text-amber-700">
                      <AlertTriangle size={10} className="mr-1 inline" />
                      This plan names individual packages, but signup currently emits ONE software
                      commission line for the whole monthly subscription with no package attached — so
                      only the catch-all software rule can be reached today. The per-package split above
                      is what the plan says, not yet what the completion pays.
                    </p>
                  )}
                  <p>
                    Devices are shown at <strong>one unit each</strong> and every finance option is listed,
                    though a merchant buys a quantity and picks one option — a deal is a template and does
                    not know either. “First year” is up front plus up to twelve monthly cycles.
                  </p>
                  <p>
                    Every figure is computed by the same functions that pay the commission
                    (<span className="font-mono">findRuleForTargets</span> +{' '}
                    <span className="font-mono">computeRuleCommission</span>). It is a projection of this
                    plan against these prices — never a promise of an amount.
                  </p>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DealCommissionSection;
